import JsonLd from '@/components/base/JsonLd';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function ShippingGuide() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://allchinabuyspreadsheet.wiki';
  const pagePath = '/allchinabuy-sheet-shipping';
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'AllChinaBuySpreadsheet Shipping Guide: Delivery Times, Costs & Tracking',
    description: 'Everything you need to know about shipping when buying from China using AllChinaBuySpreadsheet. Covers delivery time estimates, carrier options, tracking availability, customs duties, and freight forwarder tips.',
    author: { '@type': 'Organization', name: 'AllChinaBuySpreadsheet' },
    publisher: { '@type': 'Organization', name: 'AllChinaBuySpreadsheet', url: siteUrl },
    datePublished: '2026-06-25',
    dateModified: '2026-06-25',
    mainEntityOfPage: { '@type': 'WebPage', '@id': siteUrl + pagePath },
    url: siteUrl + pagePath,
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'AllChinaBuySpreadsheet Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Shipping Guide', item: siteUrl + pagePath },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={articleSchema} />
      <JsonLd data={breadcrumbSchema} />
      <Navbar />
      <header className="relative h-[420px] flex items-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Cardboard%20boxes%20stacked%20in%20modern%20warehouse%20with%20shipping%20labels%2C%20logistics%20center%2C%20organized%20inventory%2C%20bright%20industrial%20lighting%2C%20professional%20supply%20chain%20photography%2C%20clean%20efficient%20atmosphere%2C%20global%20delivery%20concept&width=1800&height=840&seq=shipping-hero-v3&orientation=landscape"
          alt="AllChinaBuySpreadsheet shipping guide"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/50"></div>
        <div className="relative z-10 w-full px-6 md:px-10 pt-16">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block px-3 py-1 bg-white/15 backdrop-blur-sm text-xs font-medium text-white rounded-full uppercase tracking-[0.15em] mb-5">Shipping</span>
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-white leading-tight">AllChinaBuySpreadsheet Shipping Guide: Delivery Times, Costs &amp; Tracking</h1>
          </div>
        </div>
      </header>
      <article className="py-16 md:py-24 px-6 md:px-10">
        <div className="max-w-3xl mx-auto">
          <p className="text-lg text-foreground-700 leading-relaxed mb-8">
            Shipping is often the biggest unknown when buying from China. Will your package arrive in two weeks or two
            months? Are there hidden customs fees? Will you get a tracking number? The
            <strong className="text-primary-600 font-semibold"> AllChinaBuySpreadsheet</strong> shipping transparency
            system answers all these questions before you buy.
          </p>
          <h2 className="text-2xl font-heading font-bold text-foreground-950 mt-14 mb-5">Understanding Shipping Options in the Spreadsheet</h2>
          <p className="text-base text-foreground-700 leading-relaxed mb-5">
            Every product in our <strong className="text-primary-600 font-semibold">china product spreadsheet list</strong>
            includes detailed shipping information: estimated delivery window, carrier options, tracking availability,
            and any known customs considerations. Unlike marketplaces that hide shipping costs until checkout, our
            <strong className="text-primary-600 font-semibold"> all china buy spreadsheet</strong> format shows you
            the full delivered price upfront, including shipping estimates based on your region.
          </p>
          <p className="text-base text-foreground-700 leading-relaxed mb-5">
            For buyers using the <strong className="text-primary-600 font-semibold">allchinabuy spreadsheet</strong> system,
            shipping costs typically range from free (on select items with standard delivery) to premium express options
            that deliver within 5-7 business days. The spreadsheet clearly marks which shipping tier each product falls
            into, so you can filter by your preferred delivery speed and budget.
          </p>
          <h2 className="text-2xl font-heading font-bold text-foreground-950 mt-14 mb-5">Customs and Import Duties Explained</h2>
          <p className="text-base text-foreground-700 leading-relaxed mb-5">
            One common concern among international buyers is unexpected customs charges. Our listings include notes on
            typical customs treatment for each product category based on destination country. While we cannot guarantee
            specific customs outcomes, the <strong className="text-primary-600 font-semibold">spreadsheet shopping guide China</strong>
            provides historical data showing what percentage of similar shipments incurred duties, helping you budget accordingly.
          </p>
          <div className="mt-14 p-8 bg-primary-50 rounded-2xl text-center">
            <h3 className="text-xl font-heading font-bold text-foreground-950 mb-3">Check Shipping for Your Region</h3>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-6 py-3 bg-foreground-950 text-background-50 text-sm font-medium rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer">
                Browse Products <i className="ri-arrow-right-line"></i>
              </a>
              <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 border border-foreground-200 text-sm font-medium text-foreground-700 rounded-full hover:bg-foreground-50 transition-colors whitespace-nowrap cursor-pointer">
                Back to Home <i className="ri-home-4-line"></i>
              </Link>
            </div>
          </div>
          <nav className="mt-12 pt-8 border-t border-background-200/70">
            <p className="text-xs font-medium text-foreground-500 uppercase tracking-wider mb-4">Related Articles</p>
            <ul className="space-y-2">
              <li><Link to="/allchinabuy-sheet-beginners" className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">Beginner's Guide: Start Shopping Today &rarr;</Link></li>
              <li><Link to="/allchinabuy-sheet-safety" className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">Safety Guide: Full Verification Process &rarr;</Link></li>
              <li><Link to="/" className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors cursor-pointer">&larr; AllChinaBuySpreadsheet Home</Link></li>
            </ul>
          </nav>
        </div>
      </article>
      <Footer />
    </div>
  );
}